import {useCallback, useEffect, useState} from 'react'
import * as Yup from 'yup'
import {Form, Formik} from 'formik'
import {QRCode} from 'antd'
import {useDispatch, useSelector} from 'react-redux'
import {Link, useHistory} from 'react-router-dom'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import * as auth from '../redux/actions'
import {IAuthState} from '../redux/reducer'
import {IErrorState} from 'src/app/modules/errors/redux/reducer'

const codeSchema = Yup.object().shape({
  code: Yup.string()
    .required('Code is required')
    .matches(/^\d{6}$/, 'Code must be exactly 6 digits'),
})

export function AuthenticatorValidation() {
  const dispatch = useDispatch()
  const history = useHistory()
  const authResp: IAuthState = useSelector((state: any) => state.auth)
  const errorState: IErrorState = useSelector((state: any) => state.globalError)

  const authenticator = authResp?.authenticator
  const accessToken = authResp?.accessToken
  const loading = authResp?.loading

  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [copied, setCopied] = useState(false)

  // The challenge is transient (never persisted). On a hard refresh it is gone,
  // so send the user back to login rather than rendering a broken form.
  useEffect(() => {
    if (!authenticator && !accessToken) {
      history.replace('/auth/login')
    }
  }, [authenticator, accessToken, history])

  useEffect(() => {
    if (submitting && !loading) {
      setSubmitting(false)
      if (!accessToken) {
        setErrorMessage(errorState?.errorMessage || 'Invalid code. Please try again.')
      }
    }
  }, [submitting, loading, accessToken, errorState?.errorMessage])

  // Drop the pending challenge before the Link navigates, otherwise the login
  // page sees authenticator.required still set and immediately pushes us back.
  const handleBackToLogin = useCallback(() => {
    dispatch(auth.actions.authenticatorCancel())
  }, [dispatch])

  const handleCopySecret = useCallback(() => {
    const secret = authenticator?.secret
    if (!secret) return
    navigator?.clipboard
      ?.writeText(secret)
      .then(() => setCopied(true))
      .catch(() => setCopied(false))
  }, [authenticator?.secret])

  if (!authenticator) {
    return null
  }

  const isSetup = authenticator.setupRequired

  return (
    <>
      <h4
        className='login-box-msg text-center mt-10 mb-3'
        data-cy='authenticator-validation-heading'
      >
        {isSetup ? 'Set up your authenticator app' : 'Two-factor authentication'}
      </h4>

      {isSetup ? (
        <div className='text-center mb-5' data-cy='authenticator-setup-section'>
          <p className='fs-6 mb-4'>
            Scan the QR code below with Google Authenticator or Microsoft Authenticator, then enter
            the 6-digit code it shows to finish setup.
          </p>

          {authenticator.otpauthUrl && (
            <div className='d-flex justify-content-center mb-4' data-cy='authenticator-qr-code'>
              <QRCode value={authenticator.otpauthUrl} size={160} bordered={false} />
            </div>
          )}

          {authenticator.secret && (
            <div className='mb-4'>
              <div className='fs-7 fw-bolder mb-2'>Can&apos;t scan? Enter this key manually:</div>
              <code
                className='d-block p-2 mb-2 border rounded bg-light text-break'
                style={{fontFamily: 'monospace', letterSpacing: '1px', fontSize: '0.85rem'}}
                data-cy='authenticator-secret'
              >
                {authenticator.secret}
              </code>
              {/* Inline width: `.login-wrapper .btn` forces width:100% and outranks utilities.
                  btn-danger is the theme's brand red ($danger #ed1b2f, $danger-inverse #fff). */}
              <button
                type='button'
                className='btn btn-sm btn-danger text-white'
                style={{width: 'auto', padding: '4px 14px', fontSize: '13px'}}
                onClick={handleCopySecret}
                data-cy='authenticator-copy-secret-button'
              >
                {copied ? 'Copied' : 'Copy key'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className='text-center fs-6 mb-4' data-cy='authenticator-verify-section'>
          Open your authenticator app and enter the current 6-digit code for your account.
        </p>
      )}

      <Formik
        initialValues={{code: ''}}
        validationSchema={codeSchema}
        onSubmit={(values: {code: string}) => {
          setErrorMessage('')
          setSubmitting(true)
          dispatch(auth.actions.authenticatorVerify(authenticator.challengeToken, values.code))
        }}
      >
        {({touched, handleChange, errors}) => (
          <Form>
            <div className='pt-4'>
              <div className=''>
                <FormTextBox
                  labelClassName='col-md-12'
                  containerClassName='col-md-12'
                  type='text'
                  placeholder='Please enter 6-digit code'
                  name='code'
                  label='Authenticator Code'
                  maxLength={6}
                  onChange={handleChange}
                  errors={errors}
                  touched={touched}
                  required={true}
                  data-cy='authenticator-code-field'
                />
              </div>

              {errorMessage && (
                <div
                  className='text-danger fw-bolder text-center mb-3'
                  data-cy='authenticator-error-message'
                >
                  {errorMessage}
                </div>
              )}

              <div className=''>
                <button
                  type='submit'
                  className='btn btn-lg w-100 mb-5'
                  disabled={loading}
                  data-cy='authenticator-submit-button'
                >
                  {!loading && (
                    <span className='indicator-label'>{isSetup ? 'Verify & Activate' : 'Verify'}</span>
                  )}

                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>

                <div className='text-center mb-5'>
                  <Link
                    to='/auth/login'
                    className='login-link fs-6 fw-bolder'
                    onClick={handleBackToLogin}
                    data-cy='authenticator-back-to-login'
                  >
                    Back to login
                  </Link>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default AuthenticatorValidation
